import { Controller } from 'react-hook-form';
import { useState } from 'react';
import styles from './AddUser.module.css';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Input, Select, Tooltip, MenuItem } from '@dsi/react-eds';
import { useUserForm } from './user-form';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import api from '../../../api/axiosInstance';
import { AxiosError } from 'axios';
import { useLoader } from '../../../hooks/useLoader';
import { AuthTypes, RoleDropdownOptions } from '../../../const/UserManagementTypes';

interface AddUserProps {
  onAdd: (user: { email: string; roles: string[]; role: string; id: string }) => void;
  existingEmails: string[];
}

function AddUser({ onAdd }: AddUserProps) {
  const {
    handleSubmit,
    formState: { errors },
    reset,
    control
  } = useUserForm();
  const { addNotifications } = useSwobNotifications();
  const { hideLoader, showLoader } = useLoader();
  const [createdPassword, setCreatedPassword] = useState<{ email: string; password: string } | null>(null);

  const generateRandomPassword = (length: number) => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';
    const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const all = upper + lower + digits + special;

    // Guarantee at least one from each category
    const mandatory = [
      upper[Math.floor(Math.random() * upper.length)],
      lower[Math.floor(Math.random() * lower.length)],
      digits[Math.floor(Math.random() * digits.length)],
      special[Math.floor(Math.random() * special.length)]
    ];

    const array = new Uint32Array(length - mandatory.length);
    window.crypto.getRandomValues(array);
    const rest = Array.from(array, (value) => all[value % all.length]);

    // Shuffle mandatory + rest together
    const combined = [...mandatory, ...rest];
    for (let i = combined.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [combined[i], combined[j]] = [combined[j], combined[i]];
    }
    return combined.join('');
  };

  //const onSubmit = async (data: { email: string; role: string }) => {
  const onSubmit = async (data: { email: string; roles?: string[]; role: string; id?: string }) => {
    showLoader();
    try {
      const newUser = {
        username: data.email,
        password: generateRandomPassword(16),
        displayName: data.email,
        email: data.email,
        roles: [data.role],
        authType: AuthTypes.Local
      };

      const response = await api.post('/api/keycloak/users', newUser);
      onAdd({ email: data.email, roles: [data.role], role: data.role, id: response.data.id });
      setCreatedPassword({ email: data.email, password: newUser.password });
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `User ${data.email} was successfully added.`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
    } catch (error: unknown) {
      let errorMsg = `An error occurred while adding the user ${data.email}.`;
      if (error instanceof AxiosError) {
        errorMsg = error?.response?.data?.message || error?.message;
      }
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: errorMsg,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      reset();
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  return (
    <>
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <div>
          <div className={styles.settingsTitle}>Add User </div>
          <div className={styles.formRow}>
            <div>
              <label htmlFor="user-email" className={styles.label}>
                Email <span className={styles.requiredAsterisk}> *</span>
                <ReactEDSWrapper component={Tooltip} message="Enter a valid Ericsson/Customer email address." pointer>
                  <span className={styles.infoIcon}>
                    <i className="icon icon-info" />
                  </span>
                </ReactEDSWrapper>
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <ReactEDSWrapper
                      component={Input}
                      id="user-email"
                      placeholder="User email"
                      className={
                        error
                          ? `${styles.inputField} ${styles.inputError} ${styles.emailInputWide}`
                          : `${styles.inputField} ${styles.emailInputWide}`
                      }
                      {...field}
                    />
                    <div className={styles.errorContainer}>
                      {error ? (
                        <span className={styles.error}>
                          <span className={styles.dangerIcon} aria-label="error" role="img">
                            &#9888;
                          </span>
                          {error.message}
                        </span>
                      ) : null}
                    </div>
                  </>
                )}
              />
            </div>
            <div>
              <label htmlFor="role" className={styles.label}>
                User Role <span className={styles.requiredAsterisk}> *</span>
                <ReactEDSWrapper
                  component={Tooltip}
                  message="Admin: User role management and configuration settings.
                Operator: read only user."
                  pointer
                >
                  <span className={styles.infoIcon}>
                    <i className="icon icon-info" />
                  </span>
                </ReactEDSWrapper>
              </label>
              <Controller
                name="role"
                control={control}
                rules={{ required: 'Role is required' }}
                render={({ field }) => (
                  <>
                    <ReactEDSWrapper
                      component={Select}
                      id="role"
                      label="Select option"
                      className={styles.selectField}
                      {...field}
                      onChange={(value) => {
                        const selected = Array.isArray(value) ? value[0] : value;
                        field.onChange(selected);
                      }}
                    >
                      <ReactEDSWrapper component={MenuItem} value={RoleDropdownOptions.Admin}>
                        {RoleDropdownOptions.Admin}
                      </ReactEDSWrapper>
                      <ReactEDSWrapper component={MenuItem} value={RoleDropdownOptions.Operator}>
                        {RoleDropdownOptions.Operator}
                      </ReactEDSWrapper>
                    </ReactEDSWrapper>
                    <div className={styles.errorContainer}>
                      {errors.role ? (
                        <span className={styles.error}>
                          <span className={styles.dangerIcon} aria-label="error" role="img">
                            &#9888;
                          </span>
                          {errors.role.message}
                        </span>
                      ) : null}
                    </div>
                  </>
                )}
              />
            </div>
            <div className={styles.flexColStart}>
              <label className={styles.invisibleLabel}>&nbsp;</label>
              <ReactEDSWrapper component={Tooltip} message="Click to add the user with the specified role." pointer>
                <span>
                  <ReactEDSWrapper
                    component={Button}
                    variant="secondary"
                    className={`btn secondary ${styles.addButton}`}
                    type="submit"
                    onClick={handleSubmit(onSubmit)}
                  >
                    <i className="icon icon-plus" /> Add
                  </ReactEDSWrapper>
                </span>
              </ReactEDSWrapper>
              <div className={styles.minHeight24}></div>
            </div>
          </div>
        </div>
      </form>
      {createdPassword && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div style={{ background: 'var(--card-color, #fff)', padding: '24px', borderRadius: '8px', maxWidth: '500px', width: '90%' }}>
            <h3 style={{ marginBottom: '16px' }}>User Created Successfully</h3>
            <p style={{ marginBottom: '8px' }}>
              Temporary password for <strong>{createdPassword.email}</strong>:
            </p>
            <div
              style={{
                background: 'var(--input-bg, #f5f5f5)',
                padding: '12px',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
                wordBreak: 'break-all',
                marginBottom: '16px'
              }}
            >
              {createdPassword.password}
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', marginBottom: '16px' }}>
              The user will be required to change this password on first login.
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                className="btn"
                onClick={() => {
                  navigator.clipboard.writeText(createdPassword.password);
                }}
              >
                Copy Password
              </button>
              <button className="btn primary" onClick={() => setCreatedPassword(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AddUser;
